import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import TextLink from 'LuxKit/TextLink'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import Plural from 'components/utils/Formatters/Plural'
import { mediaQueryUp } from 'components/utils/breakpoint'

const CardContainer = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border-radius: ${rem(8)};
  overflow: hidden;
  align-self: stretch;
  box-shadow:
    0 ${rem(1)} ${rem(1.5)} rgba(0, 0, 0, 0.07),
    0 ${rem(1.5)} ${rem(5)} rgba(0, 0, 0, 0.05),
    0 ${rem(5)} ${rem(10)} rgba(0, 0, 0, 0.05),
    0 ${rem(15)} ${rem(32)} rgba(0, 0, 0, 0.04);

  ${mediaQueryUp.tablet} {
    max-width: ${rem(383)};
  }
`

const CardHeader = styled.div`
  padding: ${rem(14)} ${rem(16)};

  ${mediaQueryUp.desktop} {
    padding: ${rem(16)} ${rem(24)};
  }

  &.tier-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
  }

  &.tier-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
  }

  &.tier-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
  }

  &.tier-platinum {
    background: linear-gradient(
      45deg,
      ${props => props.theme.palette.product.loyalty.platinum.darkForeground} 0.73%,
      ${props => props.theme.palette.product.loyalty.platinum.lightBackground} 45.77%,
      ${props => props.theme.palette.product.loyalty.platinum.darkForeground} 74.49%
    );
  }
`

const HeaderText = styled.span`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(16)};
  font-weight: ${props => props.theme.font.primary.weight.bold};
  line-height: ${rem(20)};

  ${mediaQueryUp.tablet} {
    font-size: ${rem(18)};
    line-height: ${rem(22)};
  }

  &.tier-bronze {
    color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
  }

  &.tier-silver {
    color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  }

  &.tier-gold {
    color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  }

  &.tier-platinum {
    color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
  }
`

const CardBody = styled.div`
  background: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(14)} ${rem(16)};
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};

  ${mediaQueryUp.desktop} {
    padding: ${rem(16)} ${rem(24)};
    gap: ${rem(10)};
  }
`

const RequirementsText = styled.p`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(12)};
  font-weight: ${props => props.theme.font.primary.weight.regular};
  line-height: ${rem(16)};
  color: ${props => props.theme.palette.neutral.default.one};
  margin: 0;

  ${mediaQueryUp.tablet} {
    font-size: ${rem(14)};
    line-height: ${rem(20)};
  }
`

const BoldText = styled.span`
  font-weight: ${props => props.theme.font.primary.weight.bold};
`

const ViewDetailsLinkSmall = styled(TextLink)`
  && {
    display: inline-flex;
    margin-top: ${rem(4)};
    color: ${props => props.theme.palette.neutral.default.one};
  }

  ${mediaQueryUp.tablet} {
    && {
      display: none;
    }
  }
`

const ViewDetailsLinkMedium = styled(TextLink)`
  && {
    display: none;
    margin-top: ${rem(4)};
    color: ${props => props.theme.palette.neutral.default.one};
  }

  ${mediaQueryUp.tablet} {
    && {
      display: inline-flex;
      margin-top: ${rem(8)};
    }
  }
`

const BenefitsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(6)};
  font-size: ${rem(12)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(8)};
    font-size: ${rem(14)};
  }
`

const BenefitItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(6)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(8)};
  }
`

const BenefitText = styled.span`
  font-family: ${props => props.theme.font.primary.family};
  font-size: ${rem(12)};
  font-weight: ${props => props.theme.font.primary.weight.regular};
  line-height: ${rem(16)};
  color: ${props => props.theme.palette.neutral.default.one};

  ${mediaQueryUp.tablet} {
    font-size: ${rem(14)};
    line-height: ${rem(20)};
  }
`

const BenefitIconWrapper = styled.div`
  border-radius: ${props => props.theme.borderRadius.round};
  width: ${rem(20)};
  height: ${rem(20)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${mediaQueryUp.tablet} {
    width: ${rem(24)};
    height: ${rem(24)};
  }

  &.tier-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.darkBackground};
  }

  &.tier-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.darkBackground};
  }

  &.tier-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.darkForeground};
  }

  &.tier-platinum {
    background: ${props => props.theme.palette.product.loyalty.platinum.darkBackground};
  }
`

interface TierBenefit {
  icon: React.ReactElement
  label: string
}

function getTierBenefits(
  tier: App.LoyaltyTier,
  enabledBenefits: App.LoyaltyRegionEnabledBenefits,
): Array<TierBenefit> {
  const benefits: Array<TierBenefit> = []

  if (tier === 'silver') {
    if (enabledBenefits.hotel_upgrade) {
      benefits.push({ icon: <LineCrownIcon colour="neutral-eight" size={15} />, label: '1x free room or package upgrades' })
    }
    if (enabledBenefits.lounge_pass) {
      benefits.push({ icon: <LineChairWithAirplaneIcon colour="neutral-eight" size={15} />, label: '2x free airport lounge passes' })
    }
    if (enabledBenefits.discounted_insurance) {
      benefits.push({ icon: <LineShieldCheckIcon colour="neutral-eight" size={15} />, label: '5% travel protection discount' })
    }
  } else if (tier === 'gold') {
    if (enabledBenefits.hotel_upgrade) {
      benefits.push({ icon: <LineCrownIcon colour="neutral-eight" size={15} />, label: '2x free room or package upgrades' })
    }
    if (enabledBenefits.lounge_pass) {
      benefits.push({ icon: <LineChairWithAirplaneIcon colour="neutral-eight" size={15} />, label: '2x free airport lounge passes' })
    }
    if (enabledBenefits.airport_transfer) {
      benefits.push({ icon: <LineTaxiIcon colour="neutral-eight" size={15} />, label: '2x free airport chauffeur service' })
    }
  } else if (tier === 'platinum') {
    if (enabledBenefits.hotel_upgrade) {
      benefits.push({ icon: <LineCrownIcon colour="neutral-eight" size={15} />, label: '4x free room or package upgrades' })
    }
    if (enabledBenefits.lounge_pass) {
      benefits.push({ icon: <LineChairWithAirplaneIcon colour="neutral-eight" size={15} />, label: '2x free airport lounge passes' })
    }
    if (enabledBenefits.airport_transfer) {
      benefits.push({ icon: <LineTaxiIcon colour="neutral-eight" size={15} />, label: 'Unlimited free airport chauffeur service' })
    }
  }

  return benefits
}

interface Props {
  /**
   * The tier to display benefits for
   */
  tier: App.LoyaltyTier
  /**
   * Whether this is for retaining current tier (true) or unlocking next tier (false)
   */
  isRetain?: boolean
  /**
   * Whether the member has already retained their platinum tier before the membership year ends
   * If true, the header text will be "Congratulations!"
   * If false, the header text will be "To retain [tierName]"
   */
  hasRetainedTier?: boolean
  creditsRemaining: number
  nightsRemaining: number
  expiryDate?: string
  onViewDetails?: () => void
}

function LoyaltyHeroUnlockTierCard({
  tier,
  isRetain = false,
  hasRetainedTier = false,
  creditsRemaining,
  nightsRemaining,
  expiryDate,
  onViewDetails,
}: Props) {
  const [loyaltyConfig] = useLoyaltyConfig()
  const tierName = getLoyaltyTierDisplayName(tier)

  const benefits = useMemo(() => {
    if (!loyaltyConfig) return []
    return getTierBenefits(tier, loyaltyConfig.regionEnabledBenefits)
  }, [tier, loyaltyConfig])

  const hasCredits = creditsRemaining > 0
  const hasNights = nightsRemaining > 0

  // Determine header and body text based on retain status
  let headerText: string
  let bodyText: React.ReactNode

  if (hasRetainedTier) {
    headerText = 'Congratulations!'
    bodyText = (
      <>
        You have retained {tierName} and can enjoy these benefits
        {expiryDate && <> until <FormatDateTime value={expiryDate} format="dmy-casual-short" /></>}:
      </>
    )
  } else if (isRetain) {
    headerText = `To retain ${tierName}`
    bodyText = (
      <>
        You need{' '}
        {hasCredits && <BoldText><Plural singular="Status Credit" count={creditsRemaining} withCount /></BoldText>}
        {hasCredits && hasNights && ' and '}
        {hasNights && <BoldText><Plural singular="Night" count={nightsRemaining} withCount /></BoldText>}
        {expiryDate && <> by <FormatDateTime value={expiryDate} format="dmy-casual-short" /></>}
        {' '}to retain these {tierName} benefits:
      </>
    )
  } else {
    headerText = `To unlock ${tierName}`
    bodyText = (
      <>
        You need{' '}
        {hasCredits && <BoldText><Plural singular="Status Credit" count={creditsRemaining} withCount /></BoldText>}
        {hasCredits && hasNights && ' and '}
        {hasNights && <BoldText><Plural singular="Night" count={nightsRemaining} withCount /></BoldText>}
        {expiryDate && <> by <FormatDateTime value={expiryDate} format="dmy-casual-short" /></>}
        {' '}to unlock these {tierName} benefits:
      </>
    )
  }

  if (!loyaltyConfig) return null

  return (
    <CardContainer>
      <CardHeader className={`tier-${tier}`}>
        <HeaderText className={`tier-${tier}`}>
          {headerText}
        </HeaderText>
      </CardHeader>
      <CardBody>
        <RequirementsText>
          {bodyText}
        </RequirementsText>

        <BenefitsContainer>
          {benefits.map((benefit) => (
            <BenefitItem key={benefit.label}>
              <BenefitIconWrapper className={`tier-${tier}`}>
                {benefit.icon}
              </BenefitIconWrapper>
              <BenefitText>
                {benefit.label}
              </BenefitText>
            </BenefitItem>
          ))}

          {onViewDetails && (
            <>
              <ViewDetailsLinkSmall size="small" weight="bold" underline onClick={onViewDetails}>
                View all details
              </ViewDetailsLinkSmall>
              <ViewDetailsLinkMedium size="medium" weight="bold" underline onClick={onViewDetails}>
                View all details
              </ViewDetailsLinkMedium>
            </>
          )}
        </BenefitsContainer>
      </CardBody>
    </CardContainer>
  )
}

export default LoyaltyHeroUnlockTierCard
